import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { signOut } from '../lib/supabase';
import { GoldBtn } from './UI';
import ProfileModal from './ProfileModal';

function Avatar({ username, size = 28 }) {
  const colors = ['#8B5CF6','#3B82F6','#10B981','#F59E0B','#EF4444','#EC4899','#14B8A6'];
  const bg = colors[(username?.charCodeAt(0) || 0) % colors.length];
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontSize: size * 0.42, fontWeight: 700,
      fontFamily: "'DM Mono', monospace", flexShrink: 0,
    }}>
      {(username?.[0] || '?').toUpperCase()}
    </div>
  );
}

export default function Navbar({ onAuthOpen, onRequestFilm, onAdminClick, onChatClick, onBattleClick, onRankingsClick, onTasteClick }) {
  const { profile, loading: authLoading } = useAuth();
  const [menuOpen, setMenuOpen]       = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const close = () => setMenuOpen(false);

  return (
    <>
      <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />

      <header style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(4,4,6,0.92)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid var(--border)',
      }}>
        <div style={{
          maxWidth: 1200, margin: '0 auto', padding: '0 20px',
          height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          {/* Logo */}
          <a href="/" onClick={e => { e.preventDefault(); window.history.pushState({}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate')); }}
            style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 20 }}>🎬</span>
            <span style={{ fontFamily: "'Playfair Display', serif", color: 'var(--gold)', fontSize: 20, fontWeight: 700, letterSpacing: 0.5 }}>
              <span style={{fontWeight:700}}>criti</span><span style={{color:'var(--gold)',fontWeight:800}}>Q</span><span style={{fontWeight:700}}>r</span>
            </span>
          </a>

          {/* Desktop nav */}
          <div className="nav-desktop" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <NavBtn onClick={onRankingsClick}>🏆 Rankings</NavBtn>
            <NavBtn onClick={onBattleClick}>⚔️ Battle</NavBtn>
            <NavBtn onClick={onChatClick}>💬 Chat</NavBtn>
            {onRequestFilm && profile && (
              <NavBtn onClick={onRequestFilm}>+ Request a Film</NavBtn>
            )}
            {profile?.role === 'admin' && (
              <NavBtn onClick={onAdminClick}>★ Admin</NavBtn>
            )}
            {!authLoading && !profile && (
              <GoldBtn onClick={onAuthOpen}>Log In / Sign Up</GoldBtn>
            )}
            {profile && (
              <UserChip
                profile={profile}
                onProfileOpen={() => setProfileOpen(true)}
                onTasteClick={onTasteClick}
              />
            )}
          </div>

          {/* Mobile hamburger */}
          <button className="nav-burger" onClick={() => setMenuOpen(p => !p)} style={{
            display: 'none', background: 'none', border: 'none',
            color: 'var(--text)', fontSize: 22, cursor: 'pointer', padding: 4,
          }}>
            {[0,1,2].map(i => (
              <div key={i} style={{
                width: 22, height: 2, background: 'var(--text-dim)',
                margin: i === 1 ? '5px 0' : 0, borderRadius: 2,
                transition: 'background 0.2s',
              }} />
            ))}
          </button>
        </div>

        {/* Mobile drawer */}
        <div style={{
          overflow: 'hidden',
          maxHeight: menuOpen ? 500 : 0,
          transition: 'max-height 0.28s cubic-bezier(0.4,0,0.2,1)',
          background: 'var(--surface)',
          borderTop: menuOpen ? '1px solid var(--border)' : 'none',
        }}>
          <div style={{ padding: '10px 14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>

            {profile && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--surface2)', borderRadius: 10, marginBottom: 2 }}>
                <Avatar username={profile.username} size={34} />
                <div>
                  <div style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600 }}>@{profile.username}</div>
                  {profile.role === 'admin' && <div style={{ color: 'var(--gold)', fontSize: 11 }}>★ Admin</div>}
                </div>
              </div>
            )}

            <DrawerItem onClick={() => { onRankingsClick && onRankingsClick(); close(); }}>🏆 Rankings</DrawerItem>
            <DrawerItem onClick={() => { onBattleClick && onBattleClick(); close(); }}>⚔️ Battle</DrawerItem>
            <DrawerItem onClick={() => { onChatClick && onChatClick(); close(); }}>💬 Chat</DrawerItem>
            {onRequestFilm && profile && (
              <DrawerItem onClick={() => { onRequestFilm(); close(); }}>+ Request a Film</DrawerItem>
            )}
            {profile?.role === 'admin' && (
              <DrawerItem onClick={() => { onAdminClick(); close(); }} gold>★ Admin Panel</DrawerItem>
            )}
            {profile && (
              <DrawerItem onClick={() => { onTasteClick && onTasteClick(); close(); }}>🎭 My Taste Profile</DrawerItem>
            )}
            {profile && (
              <DrawerItem onClick={() => { setProfileOpen(true); close(); }}>⚙ Account Settings</DrawerItem>
            )}
            {!authLoading && !profile && (
              <GoldBtn onClick={() => { onAuthOpen(); close(); }} style={{ marginTop: 4 }}>
                Log In / Sign Up
              </GoldBtn>
            )}
            {profile && (
              <DrawerItem onClick={async () => { await signOut(); close(); }} danger>↩ Sign Out</DrawerItem>
            )}
          </div>
        </div>
      </header>

      <style>{`
        @media (max-width: 640px) {
          .nav-desktop { display: none !important; }
          .nav-burger  { display: block !important; }
        }
      `}</style>
    </>
  );
}

function UserChip({ profile, onProfileOpen, onTasteClick }) {
  
  const [open, setOpen] = useState(false);

  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(p => !p)} style={{
        background: 'var(--surface2)', border: '1px solid var(--border2)',
        color: 'var(--text)', borderRadius: 20, padding: '4px 12px 4px 5px',
        fontSize: 13, cursor: 'pointer', fontFamily: "'Crimson Text', serif",
        display: 'flex', alignItems: 'center', gap: 7, maxWidth: 150,
      }}>
        <Avatar username={profile.username} size={22} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {profile.username}
        </span>
      </button>

      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 5 }} />
          <div style={{
            position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 10,
            background: 'var(--surface)', border: '1px solid var(--border2)',
            borderRadius: 8, minWidth: 160, overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.65)',
          }}>
            <DropItem onClick={() => { onTasteClick && onTasteClick(); setOpen(false); }}>🎭 My Taste Profile</DropItem>
            <DropItem onClick={() => { onProfileOpen(); setOpen(false); }}>⚙ Account Settings</DropItem>
            <div style={{ height: 1, background: 'var(--border)' }} />
            <DropItem onClick={async () => { await signOut(); setOpen(false); }} danger>↩ Sign Out</DropItem>
          </div>
        </>
      )}
    </div>
  );
}

function DropItem({ onClick, children, danger }) {
  return (
    <button onClick={onClick} style={{
      display: 'block', width: '100%', padding: '11px 16px',
      background: 'none', border: 'none',
      color: danger ? 'var(--red)' : 'var(--text-dim)',
      fontSize: 14, cursor: 'pointer', textAlign: 'left',
      fontFamily: "'Crimson Text', serif", transition: 'background 0.12s',
    }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
      onMouseLeave={e => e.currentTarget.style.background = 'none'}
    >{children}</button>
  );
}

function NavBtn({ onClick, children }) {
  return (
    <button onClick={onClick} style={{
      background: 'none', border: '1px solid var(--border2)',
      color: 'var(--text-dim)', borderRadius: 6, padding: '6px 12px',
      fontSize: 13, cursor: 'pointer', fontFamily: "'Crimson Text', serif",
      transition: 'border-color 0.18s, color 0.18s', whiteSpace: 'nowrap',
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor='var(--gold-dim)'; e.currentTarget.style.color='var(--gold)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border2)'; e.currentTarget.style.color='var(--text-dim)'; }}
    >{children}</button>
  );
}

function DrawerItem({ onClick, children, gold, danger }) {
  return (
    <button onClick={onClick} style={{
      width: '100%', textAlign: 'left',
      background: 'none', border: '1px solid var(--border)',
      borderRadius: 8, padding: '11px 14px', cursor: 'pointer',
      color: danger ? 'var(--red)' : gold ? 'var(--gold)' : 'var(--text-dim)',
      fontSize: 15, fontFamily: "'Crimson Text', serif",
      transition: 'background 0.14s',
    }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
      onMouseLeave={e => e.currentTarget.style.background = 'none'}
    >{children}</button>
  );
}
